import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { resolveAdminUna } from "@/lib/succession/stages";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Download, FileText } from "lucide-react";
import { toast } from "sonner";

type Variant = "full" | "incumbency";

const DEFAULT_PURPOSE =
  "The express purpose and mission of this Association is to embody and share the godly principles of Stewardship with mankind. Stewardship is more desirable than Ownership. The Association does not exist to enrich any of its Members. No Member can own the Association.";

type Values = {
  officer_name: string;
  institution_name: string;
  formation_date: string;
  una_ein: string;
  purpose_quote: string;
  appointment_date: string;
  llc_name: string;
  execution_date: string;
  execution_location: string;
};

export default function OfficerCertificates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [unaId, setUnaId] = useState<string | null>(null);
  const [unaName, setUnaName] = useState("");
  const [variant, setVariant] = useState<Variant>("full");
  const [values, setValues] = useState<Values>({
    officer_name: "",
    institution_name: "",
    formation_date: "",
    una_ein: "",
    purpose_quote: DEFAULT_PURPOSE,
    appointment_date: "",
    llc_name: "",
    execution_date: new Date().toISOString().slice(0, 10),
    execution_location: "",
  });
  const [generating, setGenerating] = useState(false);
  const [lastPath, setLastPath] = useState<string | null>(null);
  const [lastTitle, setLastTitle] = useState<string>("");

  useEffect(() => {
    (async () => {
      if (!user?.id) { setLoading(false); return; }
      const una = await resolveAdminUna(user.id);
      if (!una) { setLoading(false); return; }
      setUnaId(una.id);
      setUnaName(una.name || "");

      // Prefill from una + profile + member record + latest llc formation.
      const [unaDetail, prof, member, llc] = await Promise.all([
        supabase.from("unas").select("formation_date, ein").eq("id", una.id).maybeSingle(),
        supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle(),
        supabase
          .from("una_members")
          .select("joined_date")
          .eq("una_id", una.id).eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("llc_formations")
          .select("entity_name")
          .eq("una_id", una.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      setValues((prev) => ({
        ...prev,
        officer_name: (prof.data as any)?.full_name || prev.officer_name,
        formation_date: (unaDetail.data as any)?.formation_date || prev.formation_date,
        una_ein: (unaDetail.data as any)?.ein || prev.una_ein,
        appointment_date: (member.data as any)?.joined_date || prev.appointment_date,
        llc_name: (llc.data as any)?.entity_name || prev.llc_name,
      }));
      setLoading(false);
    })();
  }, [user?.id]);

  const requiredKeys = useMemo<Array<keyof Values>>(
    () => variant === "full"
      ? ["officer_name", "institution_name", "formation_date", "una_ein", "purpose_quote", "appointment_date", "execution_date", "execution_location"]
      : ["officer_name", "institution_name", "appointment_date", "execution_date", "execution_location"],
    [variant]
  );

  const canGenerate = requiredKeys.every((k) => String(values[k] || "").trim().length > 0);

  function set<K extends keyof Values>(key: K, v: Values[K]) {
    setValues((prev) => ({ ...prev, [key]: v }));
  }

  async function generate() {
    if (!unaId || !canGenerate) return;
    setGenerating(true);
    setLastPath(null);
    try {
      const { data, error } = await supabase.functions.invoke("officer-certificate-generate", {
        body: { una_id: unaId, variant, values },
      });
      if (error) throw error;
      const doc = (data as any)?.document;
      if (!doc?.file_path) throw new Error("No file returned");
      setLastPath(doc.file_path);
      setLastTitle(doc.document_name || "Certificate");
      toast.success("Certificate generated. It is saved to your Record.");
    } catch (e: any) {
      console.error(e);
      const msg = e?.context?.text ? await e.context.text() : (e?.message || "Generation failed");
      toast.error(typeof msg === "string" ? msg : "Generation failed");
    } finally {
      setGenerating(false);
    }
  }

  async function openLast() {
    if (!lastPath) return;
    const { data, error } = await supabase.storage
      .from("documents")
      .createSignedUrl(lastPath, 300);
    if (error || !data?.signedUrl) {
      toast.error("Could not open the file.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!unaId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <CardHeader>
            <CardTitle>Officer's Certificates</CardTitle>
            <CardDescription>
              These certificates are prepared by the Association's principal officer. Your account is not connected to an Association you administer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-semibold">Officer's Certificates</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Prepare a notarizable certificate for a bank or institution that asks for governing documents or proof of officer authority. Signed for {unaName}.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Which certificate</CardTitle>
          <CardDescription>
            Use the full certificate when the institution's compliance team escalates. Use the short form for the frequent smaller cases: new account signers, brokerages, title companies, insurers.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={variant} onValueChange={(v) => setVariant(v as Variant)} className="space-y-2">
            <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer hover:bg-muted/40">
              <RadioGroupItem value="full" id="v-full" className="mt-0.5" />
              <div>
                <div className="font-medium text-sm">Full certificate</div>
                <div className="text-xs text-muted-foreground">Purpose, standing, and incumbency. Notarized.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer hover:bg-muted/40">
              <RadioGroupItem value="incumbency" id="v-inc" className="mt-0.5" />
              <div>
                <div className="font-medium text-sm">Short form (Incumbency)</div>
                <div className="text-xs text-muted-foreground">Incumbency and authority only. Notarized.</div>
              </div>
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Certificate details</CardTitle>
          <CardDescription>
            We prefilled what we could from your profile and Association record. Review each field carefully. A licensed professional should look over anything you are unsure about.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Officer name</Label>
            <Input value={values.officer_name} onChange={(e) => set("officer_name", e.target.value)} />
          </div>
          <div>
            <Label>Institution name</Label>
            <Input
              value={values.institution_name}
              onChange={(e) => set("institution_name", e.target.value)}
              placeholder="The bank or institution, exactly as they style their name"
            />
          </div>
          <div>
            <Label>Appointment date</Label>
            <Input type="date" value={values.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">The date of the resolution appointing you as Secretary Treasurer.</p>
          </div>

          {variant === "full" && (
            <>
              <div>
                <Label>Formation date</Label>
                <Input type="date" value={values.formation_date} onChange={(e) => set("formation_date", e.target.value)} />
              </div>
              <div>
                <Label>Association EIN</Label>
                <Input
                  value={values.una_ein}
                  onChange={(e) => set("una_ein", e.target.value)}
                  placeholder="00-0000000"
                />
                <p className="text-xs text-muted-foreground mt-1">From your IRS confirmation letter.</p>
              </div>
              <div>
                <Label>Purpose (verbatim from your Constitution)</Label>
                <Textarea
                  rows={5}
                  value={values.purpose_quote}
                  onChange={(e) => set("purpose_quote", e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This must match your Constitution's express purpose article word for word. The prefilled text is the standard article; if your Constitution reads differently, correct this to match yours exactly.
                </p>
              </div>
            </>
          )}

          <div>
            <Label>Subsidiary LLC name (optional)</Label>
            <Input
              value={values.llc_name}
              onChange={(e) => set("llc_name", e.target.value)}
              placeholder="Leave blank if not applicable"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank if the certificate need not mention a subsidiary.</p>
          </div>

          <div>
            <Label>Execution date</Label>
            <Input type="date" value={values.execution_date} onChange={(e) => set("execution_date", e.target.value)} />
          </div>
          <div>
            <Label>Execution location</Label>
            <Input
              value={values.execution_location}
              onChange={(e) => set("execution_location", e.target.value)}
              placeholder="City and state where you will sign before the notary"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={generate} disabled={!canGenerate || generating}>
          {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing certificate</>) : (<><FileText className="h-4 w-4 mr-2" />Generate certificate</>)}
        </Button>
        {lastPath && (
          <Button variant="outline" onClick={openLast}>
            <Download className="h-4 w-4 mr-2" />Open {lastTitle}
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Every generated certificate is saved to your Record. Sign the final page before a California notary. For questions about how a specific institution will treat this certificate, ask a licensed professional.
      </p>
    </div>
  );
}
